import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { useSite } from "../context/SiteContext";
import { imgUrl } from "../api";

const LINKS = [
    { k: "servicios", href: "#servicios" },
    { k: "galeria", href: "#galeria" },
    { k: "testimonios", href: "#testimonios" },
    { k: "horario", href: "#horario" },
    { k: "contacto", href: "#contacto" },
];

const LangSwitch = ({ lang, setLang, testPrefix = "" }) => (
    <div className="flex items-center rounded-full border border-gold/30 overflow-hidden text-[11px] font-bold tracking-widest">
        {["es", "en"].map((l) => (
            <button
                key={l}
                data-testid={`${testPrefix}lang-${l}`}
                onClick={() => setLang(l)}
                className={`px-2.5 py-1 uppercase transition-all duration-300 ${
                    lang === l ? "bg-gold-gradient text-black" : "text-zinc-400 hover:text-gold-light"
                }`}
            >
                {l}
            </button>
        ))}
    </div>
);

export default function Header() {
    const { content, t, lang, setLang } = useSite();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                scrolled ? "glass border-b border-gold/20 py-3" : "bg-transparent py-5"
            }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 flex items-center justify-between gap-4">
                <a href="#inicio" data-testid="nav-logo" className="flex items-center gap-3 group min-w-0">
                    {content?.logo_url && (
                        <img
                            src={imgUrl(content.logo_url)}
                            alt={content?.business_name || "Logo"}
                            className="h-10 w-10 rounded-full object-cover ring-1 ring-gold/50 transition-transform duration-500 group-hover:rotate-12 shrink-0"
                        />
                    )}
                    <span className="font-serif text-lg tracking-wide text-amber-50 truncate">
                        {content?.business_name || ""}
                    </span>
                </a>

                <nav className="hidden lg:flex items-center gap-8">
                    {LINKS.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            data-testid={`nav-link-${l.k}`}
                            className="text-sm text-zinc-300 hover:text-gold transition-colors duration-300 tracking-wide"
                        >
                            {t.nav[l.k]}
                        </a>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-4">
                    <LangSwitch lang={lang} setLang={setLang} />
                    <a
                        href={`tel:${(content?.phone || "").replace(/[^\d+]/g, "")}`}
                        data-testid="header-phone"
                        className="flex items-center gap-2 text-sm text-zinc-300 hover:text-gold transition-colors"
                    >
                        <Phone size={15} className="text-gold" />
                        {content?.phone || ""}
                    </a>
                    <a
                        href="#contacto"
                        data-testid="header-cta"
                        className="bg-gold-gradient text-black text-sm font-semibold px-5 py-2.5 rounded-full hover:shadow-[0_0_25px_rgba(212,175,55,0.4)] hover:-translate-y-0.5 transition-all duration-300"
                    >
                        {t.book}
                    </a>
                </div>

                <div className="flex lg:hidden items-center gap-3">
                    <LangSwitch lang={lang} setLang={setLang} testPrefix="mobile-" />
                    <button
                        data-testid="mobile-menu-toggle"
                        className="text-gold p-2"
                        onClick={() => setOpen(!open)}
                        aria-label="Menú"
                    >
                        {open ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="lg:hidden glass border-b border-gold/20 overflow-hidden"
                        data-testid="mobile-menu"
                    >
                        <div className="px-6 py-6 flex flex-col gap-4">
                            {LINKS.map((l) => (
                                <a
                                    key={l.href}
                                    href={l.href}
                                    data-testid={`mobile-nav-link-${l.k}`}
                                    onClick={() => setOpen(false)}
                                    className="font-serif text-xl text-amber-50 hover:text-gold transition-colors"
                                >
                                    {t.nav[l.k]}
                                </a>
                            ))}
                            <a
                                href="#contacto"
                                data-testid="mobile-header-cta"
                                onClick={() => setOpen(false)}
                                className="mt-2 bg-gold-gradient text-black text-sm font-semibold px-5 py-3 rounded-full text-center"
                            >
                                {t.book}
                            </a>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
}
